import { useState, useEffect, useCallback } from 'react';
import { offlineSyncQueue } from '@/utils/offlineSync';
import { getPendingActions } from '@/utils/offlineQueue';
import { OFFLINE_QUEUED_EVENT } from '@/utils/offlineRequestQueue';

export interface OfflineSyncStats {
  pending: number;
  failed: number;
  total: number;
}

export interface OfflineSyncResult {
  synced: number;
  failed: number;
}

interface UseOfflineSyncReturn {
  isOnline: boolean;
  isSyncing: boolean;
  /** Conteggio azioni in coda (alias legacy di stats.pending) */
  pendingCount: number;
  /** Statistiche dettagliate sullo stato della coda */
  stats: OfflineSyncStats | null;
  /** Flush manuale con risultato { synced, failed } */
  sync: () => Promise<OfflineSyncResult | null>;
  /** @deprecated Usa sync() — mantenuto per retrocompatibilità */
  forceSync: () => Promise<void>;
}

export interface UseOfflineSyncOptions {
  /**
   * Stato di rete fornito dall'esterno (es. `NetworkProvider`, che su Android
   * usa @capacitor/network — più affidabile di `navigator.onLine`, che in
   * WebView resta spesso `true` anche senza connettività reale).
   * Se omesso si usa `navigator.onLine`.
   */
  onlineOverride?: boolean;
}

/**
 * useOfflineSync
 *
 * Monitora la connettività di rete e svuota automaticamente la coda
 * offline quando la connessione viene ripristinata (o quando lo stato di
 * rete fornito dall'esterno passa a "online").
 *
 * Espone sia l'API storica (pendingCount / forceSync) sia la nuova
 * (stats / sync) richiesta da OfflineBanner.
 *
 * Canonical path: import { useOfflineSync } from '@/hooks/infrastructure/useOfflineSync';
 */
export function useOfflineSync(options: UseOfflineSyncOptions = {}): UseOfflineSyncReturn {
  const onlineOverride = options.onlineOverride;
  const [windowOnline, setWindowOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [stats, setStats] = useState<OfflineSyncStats | null>(null);

  const isOnline = onlineOverride ?? windowOnline;

  // Aggiorna le statistiche leggendo direttamente la coda
  const refreshStats = useCallback(async () => {
    try {
      const actions = await getPendingActions();
      const pending = actions.filter((a) => a.status === 'pending' || a.status === 'syncing').length;
      const failed  = actions.filter((a) => a.status === 'failed').length;
      setStats({ pending, failed, total: actions.length });
    } catch {
      setStats(null);
    }
  }, []);

  // Flush con risultato strutturato
  const sync = useCallback(async (): Promise<OfflineSyncResult | null> => {
    const online =
      onlineOverride ?? (typeof navigator !== 'undefined' ? navigator.onLine : true);
    if (!online) return null;

    const before = await getPendingActions();
    if (before.length === 0) return { synced: 0, failed: 0 };

    setIsSyncing(true);
    try {
      await offlineSyncQueue.flush();
      const after = await getPendingActions();
      const failedAfter = after.filter((a) => a.status === 'failed').length;
      const synced = before.length - after.length;
      await refreshStats();
      return { synced, failed: failedAfter };
    } catch (err) {
      console.error('[useOfflineSync] Sync error:', err);
      return null;
    } finally {
      setIsSyncing(false);
    }
  }, [refreshStats, onlineOverride]);

  // API legacy
  const forceSync = useCallback(async (): Promise<void> => {
    await sync();
  }, [sync]);

  useEffect(() => {
    refreshStats();
  }, [refreshStats]);

  // Una nuova scrittura appena accodata deve comparire subito nel banner,
  // senza attendere il prossimo ciclo di rete.
  useEffect(() => {
    const handleQueued = () => {
      void refreshStats();
    };
    window.addEventListener(OFFLINE_QUEUED_EVENT, handleQueued);
    return () => window.removeEventListener(OFFLINE_QUEUED_EVENT, handleQueued);
  }, [refreshStats]);

  // Flush automatico: all'avvio dell'app se la coda non è vuota e al ritorno
  // della connessione. Il replay è idempotente (ogni azione viene rimossa solo
  // dopo il successo), quindi un doppio trigger non applica nulla due volte.
  useEffect(() => {
    if (!isOnline) return;
    let cancelled = false;

    const flushIfPending = async () => {
      try {
        const actions = await getPendingActions();
        if (cancelled || actions.length === 0) return;
        await sync();
      } catch {
        /* coda non leggibile: nessun flush */
      }
    };

    flushIfPending();

    const handleOnline = () => {
      setWindowOnline(true);
      if (onlineOverride === undefined) flushIfPending();
    };
    const handleOffline = () => setWindowOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isOnline, sync, onlineOverride]);

  const pendingCount = stats?.pending ?? 0;

  return { isOnline, isSyncing, pendingCount, stats, sync, forceSync };
}
